/**
 * 고용형태 변경 모달 (V45)
 *
 * 파견직 → 계약직 → 정규직 등 고용형태 변경에 사용됩니다.
 */

import React, { useState, useEffect } from 'react';
import { X, Calendar, AlertCircle, ArrowRight } from 'lucide-react';
import { useModalOverlayClose } from '../../../../shared/hooks/useModalOverlayClose';
import {
  EmploymentManagementItem,
  EmploymentTypeCode,
} from '../../../../shared/hooks/useEmploymentManagement';

interface EmploymentTypeChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (newTypeCode: EmploymentTypeCode, effectiveDate: string, reason?: string, conversionBonusYears?: number) => Promise<void>;
  item: EmploymentManagementItem | null;
  isLoading: boolean;
}

// 고용형태 옵션
const EMPLOYMENT_TYPE_OPTIONS: { code: EmploymentTypeCode; name: string }[] = [
  { code: 'DISPATCH', name: '파견직' },
  { code: 'INTERN', name: '인턴' },
  { code: 'CONTRACT', name: '계약직' },
  { code: 'REGULAR', name: '정규직' },
];

export function EmploymentTypeChangeModal({
  isOpen,
  onClose,
  onSubmit,
  item,
  isLoading,
}: EmploymentTypeChangeModalProps) {
  const overlayProps = useModalOverlayClose(onClose);
  const [newTypeCode, setNewTypeCode] = useState<EmploymentTypeCode>('CONTRACT');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');
  const [conversionBonusYears, setConversionBonusYears] = useState<number>(0);
  const [error, setError] = useState('');

  // 모달 열릴 때 초기화
  useEffect(() => {
    if (isOpen && item) {
      // 기본값: 현재 고용형태와 다른 첫 번째 옵션
      const otherOptions = EMPLOYMENT_TYPE_OPTIONS.filter(
        (opt) => opt.code !== item.employmentTypeCode
      );
      setNewTypeCode(otherOptions[0]?.code || 'REGULAR');

      // 기본 적용일: 오늘
      setEffectiveDate(new Date().toISOString().split('T')[0]);
      setReason('');
      setConversionBonusYears(0);
      setError('');
    }
  }, [isOpen, item]);

  // 최소 선택 가능 날짜 (오늘)
  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // 고용형태 이름 찾기
  const getTypeName = (code: EmploymentTypeCode) => {
    return EMPLOYMENT_TYPE_OPTIONS.find((opt) => opt.code === code)?.name || code;
  };

  // 제출 처리
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 유효성 검증
    if (!newTypeCode) {
      setError('변경할 고용형태를 선택해주세요.');
      return;
    }

    if (!effectiveDate) {
      setError('적용일을 선택해주세요.');
      return;
    }

    const today = getMinDate();
    if (effectiveDate < today) {
      setError('적용일은 오늘 이후여야 합니다.');
      return;
    }

    if (newTypeCode === item?.employmentTypeCode) {
      setError('현재 고용형태와 동일합니다.');
      return;
    }

    // V61: 전환가산 검증 (정규직 전환 시에만)
    if (newTypeCode === 'REGULAR' && conversionBonusYears < 0) {
      setError('전환가산은 음수일 수 없습니다.');
      return;
    }

    try {
      // V61: 정규직 전환 시에만 conversionBonusYears 전달
      const bonusToSend = newTypeCode === 'REGULAR' && conversionBonusYears > 0 ? conversionBonusYears : undefined;
      await onSubmit(newTypeCode, effectiveDate, reason || undefined, bonusToSend);
    } catch (err: any) {
      setError(err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  if (!isOpen || !item) return null;

  // 선택 가능한 고용형태 (현재 고용형태 제외)
  const availableOptions = EMPLOYMENT_TYPE_OPTIONS.filter(
    (opt) => opt.code !== item.employmentTypeCode
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      {...overlayProps}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>고용형태 변경</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 본문 */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px' }}>
            {/* 직원 정보 */}
            <div
              style={{
                background: 'var(--color-gray-50)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
              }}
            >
              <div style={{ fontSize: '14px', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
                대상 직원
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600 }}>{item.employeeName}</div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                {item.orgUnitName}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '8px',
                  fontSize: '14px',
                }}
              >
                <span
                  style={{
                    padding: '4px 10px',
                    background: 'var(--color-primary-lighter)',
                    borderRadius: '4px',
                    fontWeight: 500,
                  }}
                >
                  {item.employmentTypeName}
                </span>
                <ArrowRight size={16} color="var(--color-gray-400)" />
                <span
                  style={{
                    padding: '4px 10px',
                    background: '#dcfce7',
                    borderRadius: '4px',
                    fontWeight: 500,
                    color: '#166534',
                  }}
                >
                  {getTypeName(newTypeCode)}
                </span>
              </div>
            </div>

            {/* 새 고용형태 */}
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                }}
              >
                변경할 고용형태 *
              </label>
              <select
                value={newTypeCode}
                onChange={(e) => setNewTypeCode(e.target.value as EmploymentTypeCode)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  background: 'white',
                }}
              >
                {availableOptions.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 적용일 */}
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                }}
              >
                적용일 *
              </label>
              <div style={{ position: 'relative' }}>
                <Calendar
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--color-gray-400)',
                  }}
                />
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  min={getMinDate()}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 40px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            {/* V20-FIX: 정규직 전환 시 이전경력 입력 (priorCareerYears 누적) */}
            {newTypeCode === 'REGULAR' && (
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '8px',
                  }}
                >
                  이전경력 (선택)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="number"
                    value={conversionBonusYears}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      setConversionBonusYears(isNaN(val) ? 0 : val);
                    }}
                    min={0}
                    step={1}
                    style={{
                      width: '100px',
                      padding: '10px 12px',
                      border: '1px solid var(--color-gray-300)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      textAlign: 'right',
                    }}
                  />
                  <span style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>년</span>
                </div>
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '12px',
                    color: 'var(--color-gray-500)',
                  }}
                >
                  정규직 전환 시 인정받는 경력 가산 연한 (기존 가산에 누적됨)
                </div>
              </div>
            )}

            {/* 사유 */}
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                }}
              >
                변경 사유 (선택)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="고용형태 변경 사유를 입력해주세요"
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#dc2626',
                  fontSize: '13px',
                }}
              >
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* 안내 메시지 */}
            <div
              style={{
                marginTop: '16px',
                padding: '12px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#1e40af',
              }}
            >
              <strong>안내:</strong> 고용형태 변경 시 기존 이력이 종료되고 새 이력이 생성됩니다.
              적용일 전날까지 기존 고용형태로, 적용일부터 새 고용형태로 기록됩니다.
            </div>
          </div>

          {/* 푸터 */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--color-gray-200)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                background: 'white',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              {isLoading ? '처리 중...' : '고용형태 변경'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
